import React, { useState, useRef, useEffect, useCallback } from 'react';
import { productAPI, parsePaginated } from '../services/api';
import Pagination, { PAGE_SIZE } from './Pagination';

function Products() {
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Utility function to clean text data and prevent Unicode encoding issues
  const cleanTextData = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    return text
      // Replace problematic Unicode characters with their ASCII equivalents
      .replace(/[\u2013\u2014]/g, '-') // Replace en dash and em dash with regular dash
      .replace(/[\u2018\u2019]/g, "'") // Replace smart quotes with regular quotes
      .replace(/[\u201C\u201D]/g, '"') // Replace smart double quotes with regular quotes
      .replace(/[\u2026]/g, '...') // Replace ellipsis with three dots
      .replace(/[\u00A0]/g, ' ') // Replace non-breaking space with regular space
      .replace(/[\u2022]/g, '•') // Replace bullet with regular bullet
      .replace(/[\u00B0]/g, '°') // Replace degree symbol with regular degree
      .replace(/[\u00AE]/g, '(R)') // Replace registered trademark with (R)
      .replace(/[\u2122]/g, '(TM)') // Replace trademark with (TM)
      .replace(/[\u00A9]/g, '(C)') // Replace copyright with (C)
      // Handle accented characters and other Unicode issues
      .replace(/[\u00E9\u00E8\u00EA\u00EB]/g, 'e') // Replace é, è, ê, ë with e
      .replace(/[\u00E0\u00E1\u00E2\u00E3\u00E4\u00E5]/g, 'a') // Replace à, á, â, ã, ä, å with a
      .replace(/[\u00EC\u00ED\u00EE\u00EF]/g, 'i') // Replace ì, í, î, ï with i
      .replace(/[\u00F2\u00F3\u00F4\u00F5\u00F6\u00F8]/g, 'o') // Replace ò, ó, ô, õ, ö, ø with o
      .replace(/[\u00F9\u00FA\u00FB\u00FC]/g, 'u') // Replace ù, ú, û, ü with u
      .replace(/[\u00F1]/g, 'n') // Replace ñ with n
      .replace(/[\u00E7]/g, 'c') // Replace ç with c
      .replace(/[\u00DF]/g, 'ss') // Replace ß with ss
      .trim();
  };
  
  // State for modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [newProduct, setNewProduct] = useState({
    title: '',
    product_name: '',
    category: 'Hair Care',
    brand: '',
    description: '',
    how_to_use: '',
    rate: '',
    imageFiles: [],
    imagePreview: null,
    youtube_video_url: ''
  });

  // Fetch products from API
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await productAPI.getProducts({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
      });
      const parsed = parsePaginated(response.data);
      setProducts(parsed.results);
      setTotalCount(parsed.count);
      if (parsed.results.length === 0 && parsed.count > 0 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  // Helper function to get the first image URL from a product
  const getProductImageUrl = (product) => {
    if (!product.images || product.images.length === 0) {
      return null;
    }
    
    const firstImage = product.images[0];
    
    // Handle different possible image object structures
    if (typeof firstImage === 'string') {
      return firstImage;
    } else if (firstImage && typeof firstImage === 'object') {
      // Try different possible property names for the image URL
      const imageUrl = firstImage.image || firstImage.url || firstImage.src || firstImage.file || firstImage.path || null;
      
      if (imageUrl) {
        // If the URL is relative (doesn't start with http), prepend the API base URL
        if (!imageUrl.startsWith('http')) {
          const baseUrl = 'https://mountvoq.backend.tqdemo.website';
          return baseUrl + imageUrl;
        }
        // Add cache-busting parameter to prevent browser caching
        const separator = imageUrl.includes('?') ? '&' : '?';
        return imageUrl + separator + 't=' + Date.now();
      }
      
      // If image is null but we have an ID, try to construct the URL
      if (!imageUrl && firstImage.id) {
        const baseUrl = 'https://mountvoq.backend.tqdemo.website';
        return baseUrl + `/media/product_images/${firstImage.id}/?t=${Date.now()}`;
      }
    }
    
    return null;
  };

  // Helper function to extract image path from URL for replace_images
  const extractImagePath = (imageUrl) => {
    if (!imageUrl) return null;
    
    // Try different patterns to extract the path
    const patterns = [
      /\/media\/(.+)/,
      /\/api\/(.+)/,
      /\/static\/(.+)/,
      /\/uploads\/(.+)/
    ];
    
    for (const pattern of patterns) {
      const match = imageUrl.match(pattern);
      if (match) {
        // Clean up the path and ensure it's in the correct format
        let path = match[1];
        // Remove any query parameters
        path = path.split('?')[0];
        // Remove any trailing slashes
        path = path.replace(/\/$/, '');
        return path;
      }
    }
    
    // If no pattern matches, try to extract just the filename
    const filename = imageUrl.split('/').pop();
    if (filename && filename.includes('.')) {
      return filename;
    }
    
    // If no pattern matches, return the full URL as fallback
    return imageUrl;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setCurrentPage(1);
        }
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);





  // Handle image upload for new product
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProduct(prevState => ({
          ...prevState,
          imageFiles: files,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(files[0]);
    }
  };

  // Handle image upload for editing product
  const handleEditImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Create preview for the first image
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingProduct(prevState => ({
          ...prevState,
          imageFiles: files,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(files[0]);
    }
  };

  // Handle adding a new product
  const handleAddProduct = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      
      formData.append('title', cleanTextData(newProduct.title));
      formData.append('product_name', cleanTextData(newProduct.product_name));
      formData.append('category', cleanTextData(newProduct.category) || 'Hair Care');
      formData.append('brand', cleanTextData(newProduct.brand));
      formData.append('description', cleanTextData(newProduct.description));
      formData.append('how_to_use', cleanTextData(newProduct.how_to_use));
      formData.append('rate', cleanTextData(newProduct.rate));
      formData.append('youtube_video_url', cleanTextData(newProduct.youtube_video_url));
      
      if (newProduct.imageFiles && newProduct.imageFiles.length > 0) {
        formData.append('uploaded_images', newProduct.imageFiles[0]);
      }
      

      
      await productAPI.addProduct(formData);
      fetchProducts(); // Refresh the list
      setShowAddModal(false);
      setNewProduct({
        title: '',
        product_name: '',
        category: 'Hair Care',
        brand: '',
        description: '',
        how_to_use: '',
        rate: '',
        imageFiles: [],
        imagePreview: null,
        youtube_video_url: ''
      });
    } catch (err) {
      console.error("Error adding product:", err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to add product';
      if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please check your input and try again.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check your input.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (product) => {
    setError(null); // Clear any previous errors
    setEditingProduct({
      id: product.id,
      title: product.title || '',
      product_name: product.product_name || '',
      category: product.category || '',
      brand: product.brand || '',
      description: product.description || '',
      how_to_use: product.how_to_use || '',
      rate: product.rate || '',
      youtube_video_url: product.youtube_video_url || '',
      images: product.images || [],
      imageFiles: [],
      imagePreview: null
    });
    setShowEditModal(true);
  };

  // Handle saving edited product
  const handleSaveEdit = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      
      formData.append('title', cleanTextData(editingProduct.title));
      formData.append('product_name', cleanTextData(editingProduct.product_name));
      formData.append('category', cleanTextData(editingProduct.category));
      formData.append('brand', cleanTextData(editingProduct.brand));
      formData.append('description', cleanTextData(editingProduct.description));
      formData.append('how_to_use', cleanTextData(editingProduct.how_to_use));
      formData.append('rate', cleanTextData(editingProduct.rate));
      formData.append('youtube_video_url', cleanTextData(editingProduct.youtube_video_url));
      
      // Handle image replacement
      if (editingProduct.imageFiles && editingProduct.imageFiles.length > 0) {
        // First, add the IDs of existing images to be deleted
        const currentImageUrl = getProductImageUrl(editingProduct);
        if (currentImageUrl && editingProduct.images && editingProduct.images.length > 0) {
          editingProduct.images.forEach((img, index) => {
            if (img.id) {
              formData.append('delete_images', img.id);
            }
          });
        }
        
        // Try multiple approaches for adding new images
        editingProduct.imageFiles.forEach((file, index) => {
          // Approach 1: Use uploaded_images (most common field name)
          formData.append('uploaded_images', file);
          
          // Approach 2: Use replace_images as specified in API docs
          formData.append('replace_images', file);
          
          // Approach 3: Use images field
          formData.append('images', file);
        });
      }
      

      
      try {
        const response = await productAPI.updateProduct(editingProduct.id, formData);
        
        // If we have new images and the update didn't include them, try a separate image upload
        if (editingProduct.imageFiles && editingProduct.imageFiles.length > 0) {
          const updatedProduct = response.data;
          if (!updatedProduct.images || updatedProduct.images.length === 0) {
            // Create a new form data for just the images
            const imageFormData = new FormData();
            editingProduct.imageFiles.forEach((file, index) => {
              imageFormData.append('uploaded_images', file);
            });
            
            // Try to add images separately
            try {
              await productAPI.updateProduct(editingProduct.id, imageFormData);
            } catch (imageError) {
              console.error("Separate image upload failed:", imageError);
            }
          }
        }
        
        // Force refresh the products list to get updated images
        setTimeout(() => {
          fetchProducts(); // Refresh the list after a short delay
        }, 1000);
      } catch (error) {
        console.error("Product update failed:", error);
        if (error.response) {
          console.error("Error response:", error.response.data);
          console.error("Error status:", error.response.status);
        }
        throw error; // Re-throw to be caught by the outer try-catch
      }
      setShowEditModal(false);
      setEditingProduct(null);
    } catch (err) {
      console.error("Error updating product:", err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to update product';
      if (err.response?.status === 500) {
        // Check for Unicode encoding errors
        if (err.response?.data?.details && err.response.data.details.includes('ascii codec')) {
          errorMessage = 'Unicode encoding error detected. Please check for special characters in your text fields.';
        } else {
          errorMessage = 'Server error occurred. Please check your input and try again.';
        }
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check your input.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  // Handle confirming deletion
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await productAPI.deleteProduct(productToDelete.id);
      fetchProducts(); // Refresh the list
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Error deleting product:", err);
      setError(`Failed to delete product: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const pageProducts = Array.isArray(products) ? products : [];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Products</h1>
        <button 
          onClick={() => {
            setError(null); // Clear any previous errors
            setShowAddModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Add New Product
        </button>
      </div>
      
      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search products..."
          className="w-full p-2 border rounded"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-3 text-center py-10">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2">Loading products...</p>
          </div>
        ) : error ? (
          <div className="col-span-3 text-center py-10 text-red-500">
            {error}
          </div>
        ) : pageProducts.length === 0 ? (
          <div className="col-span-3 text-center py-10 text-gray-500">
            No products found.
          </div>
        ) : (
          pageProducts.map(product => (
            <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {(() => {
                const imageUrl = getProductImageUrl(product);
                return imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={product.title} 
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null;
              })()}
              <div className={`flex items-center justify-center h-48 bg-gray-100 ${getProductImageUrl(product) ? 'hidden' : ''}`}>
                <div className="text-center">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <p className="text-sm text-gray-500">{product.title}</p>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-1">{product.title}</h3>
                <p className="text-sm text-gray-600 mb-1">Product: {product.product_name}</p>
                <p className="text-sm text-gray-600 mb-1">Brand: {product.brand}</p>
                <p className="text-sm text-gray-600 mb-2">Category: {product.category}</p>
                <p className="text-sm text-gray-600 mb-2">Price: ₹{product.rate}</p>
                <p className="text-sm mb-2 line-clamp-2">{product.description}</p>
                <div className="flex justify-end mt-4 space-x-2">
                  <button
                    onClick={() => handleEditClick(product)}
                    className="bg-blue-100 text-blue-600 px-3 py-1 rounded hover:bg-blue-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteClick(product)}
                    className="bg-red-100 text-red-600 px-3 py-1 rounded hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 rounded-lg overflow-hidden shadow">
        <Pagination page={currentPage} total={totalCount} onPageChange={setCurrentPage} />
      </div>
      
      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Add New Product</h2>
            
            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                <p className="text-sm">{error}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newProduct.title}
                  onChange={(e) => setNewProduct({...newProduct, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Product Name *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newProduct.product_name}
                  onChange={(e) => setNewProduct({...newProduct, product_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Brand</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newProduct.brand}
                  onChange={(e) => setNewProduct({...newProduct, brand: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Price (₹)</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newProduct.rate}
                  onChange={(e) => setNewProduct({...newProduct, rate: e.target.value})}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
                <textarea
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">How to Use</label>
                <textarea
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  value={newProduct.how_to_use}
                  onChange={(e) => setNewProduct({...newProduct, how_to_use: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="url"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={newProduct.youtube_video_url}
                  onChange={(e) => setNewProduct({...newProduct, youtube_video_url: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">Product Image</label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Choose Image
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <span className="text-sm text-gray-500">
                    {newProduct.imageFiles && newProduct.imageFiles.length > 0 
                      ? 'Image selected' 
                      : 'No file chosen'}
                  </span>
                </div>
                {newProduct.imagePreview && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-2">Image Preview:</p>
                    <img
                      src={newProduct.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleAddProduct}
                disabled={!newProduct.title?.trim() || !newProduct.product_name?.trim()}
              >
                {isLoading ? 'Adding...' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Edit Product</h2>
            
            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                <p className="text-sm">{error}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingProduct.title}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, title: e.target.value}))}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Product Name</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingProduct.product_name}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, product_name: e.target.value}))}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingProduct.category}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, category: e.target.value}))}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Brand</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingProduct.brand}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, brand: e.target.value}))}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Price (₹)</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingProduct.rate}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, rate: e.target.value}))}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="url"
                  className="w-full p-2 border rounded"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={editingProduct.youtube_video_url}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, youtube_video_url: e.target.value}))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="3"
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, description: e.target.value}))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">How to Use</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="3"
                  value={editingProduct.how_to_use}
                  onChange={(e) => setEditingProduct(prevState => ({...prevState, how_to_use: e.target.value}))}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">Product Image</label>
                
                {/* Current Image Display */}
                {(() => {
                  const currentImageUrl = getProductImageUrl(editingProduct);
                  return currentImageUrl ? (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Current Image:</p>
                      <img
                        src={currentImageUrl}
                        alt="Current product"
                        className="h-32 w-32 object-cover rounded border"
                      />
                    </div>
                  ) : null;
                })()}
                
                {/* Image Upload */}
                <div className="flex items-center space-x-4">
                                      <button
                      type="button"
                      onClick={() => editFileInputRef.current.click()}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      {getProductImageUrl(editingProduct) ? 'Replace Images' : 'Upload Images'}
                    </button>
                                      <input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleEditImageUpload}
                    />
                                      <span className="text-sm text-gray-500">
                      {editingProduct.imageFiles && editingProduct.imageFiles.length > 0 
                        ? `${editingProduct.imageFiles.length} image(s) selected` 
                        : 'Choose image file(s)'}
                    </span>
                </div>
                
                {/* New Image Preview */}
                {editingProduct.imagePreview && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-2">New Image Preview:</p>
                    <img
                      src={editingProduct.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6 space-x-2">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingProduct(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded border-2 ${
                  !editingProduct.title?.trim() || !editingProduct.product_name?.trim() || isLoading
                    ? 'bg-gray-400 text-gray-600 border-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                }`}
                onClick={handleSaveEdit}
                disabled={!editingProduct.title?.trim() || !editingProduct.product_name?.trim() || isLoading}
                style={{ minWidth: '120px', minHeight: '40px' }}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && productToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-medium mb-4">Confirm Deletion</h3>
            <p className="mb-4">Are you sure you want to delete "{productToDelete.title}"? This action cannot be undone.</p>
            <div className="flex justify-end">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;









